import { CloseOutlined } from '@ant-design/icons';
import { Button, Form, Switch } from 'antd';
import { useCallback, useContext, useMemo } from 'react';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { OpeningHoursType } from 'app/typings/core/admin/experiences/openingHours/openingHours';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import { capitalizeFirstLetter } from 'app/utils/string';

import { TimeSlotsEditor } from './TimeSlotsEditor';
import { DEFAULT_SLOT } from './utils';

export const OpeningHoursDaily = () => {
  const { isViewMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const formValues = Form.useWatch('openingHours', form);

  const days = useMemo(() => formValues?.days || {}, [formValues]);

  const setDay = useCallback(
    (
      day: DayOfWeek,
      value: {
        isAllDay: boolean;
        slots?: { start: string; end: string }[];
      }
    ) => {
      const newDays = { ...days, [day]: value };

      form.setFieldValue(['openingHours', 'days'], newDays);
      form.setFieldValue(['openingHours', 'type'], OpeningHoursType.DAILY);
    },
    [days, form]
  );

  const handleDayEnabled = useCallback(
    (day: DayOfWeek, enabled: boolean) => {
      if (!enabled) {
        setDay(day, { isAllDay: false });
      } else {
        setDay(day, {
          slots: [DEFAULT_SLOT],
          isAllDay: false,
        });
      }
    },
    [setDay]
  );

  const handleAllDay = useCallback(
    (day: DayOfWeek, isAllDay: boolean) => {
      setDay(
        day,
        isAllDay
          ? { isAllDay: true }
          : {
              isAllDay: false,
              slots: [DEFAULT_SLOT],
            }
      );
    },
    [setDay]
  );

  return (
    <div className="daily-opening-hours">
      {Object.values(DAY_OF_WEEK).map((day) => {
        const enabled =
          !!days[day] && (days[day].isAllDay || days[day].slots?.length > 0);
        const isAllDay = days[day]?.isAllDay;

        return (
          <div key={day} className="daily-opening-hours-day">
            <div className="daily-opening-hours-day-header">
              <Switch
                checked={enabled}
                size="small"
                disabled={isViewMode}
                onChange={(checked) => handleDayEnabled(day, checked)}
              />
              <span className="day-name">{capitalizeFirstLetter(day)}</span>
            </div>
            {enabled &&
              (isAllDay ? (
                <Button
                  onClick={() => handleAllDay(day, false)}
                  className="allday-label"
                  icon={<CloseOutlined />}
                  disabled={isViewMode}
                >
                  24/24
                </Button>
              ) : (
                <TimeSlotsEditor
                  timeSlotName={['openingHours', 'days', day, 'slots']}
                  onAllDayClick={() => handleAllDay(day, true)}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
};
